import {
    POST_SPIROMETRY_ENDPOINT,
    PRE_SPIROMETRY_ENDPOINT,
} from "../../endpoints";
import { Nullable } from "../../types";
import { fetcher } from "../../utils";
import {
    convertPostSpirometryDataFromAPI,
    convertPreSpirometryDataFromAPI,
} from "./converters";
import { PostSpirometryData, PreSpirometryData } from "./types";

export const getPreSpirometryData = async ({ testId }: { testId: string }) => {
    const response = await fetcher(PRE_SPIROMETRY_ENDPOINT, {
        params: { test_id: testId },
    });

    return convertPreSpirometryDataFromAPI(response);
};

export const validatePreSpirometryField = async ({
    testId,
    fieldId,
    value,
}: {
    testId: string;
    fieldId: string;
    value: Nullable<number>;
}) => {
    const response = await fetcher(
        `${PRE_SPIROMETRY_ENDPOINT}/validation?test_id=${testId}`,
        {
            method: "POST",
            body: JSON.stringify({
                [fieldId]: { measured: value },
            }),
            headers: {
                "Content-Type": "application/json",
            },
        },
        true
    );

    return response;
};

export const submitPreSprimetryData = async ({
    testId,
    values,
}: {
    testId: string;
    values: Partial<PreSpirometryData>;
}) => {
    const response = await fetcher(
        PRE_SPIROMETRY_ENDPOINT,
        {
            method: "PUT",
            body: JSON.stringify(values),
            headers: {
                "Content-Type": "application/json",
            },
            params: { test_id: testId },
        },
        true
    );

    return convertPreSpirometryDataFromAPI(response);
};

export const getPostSpirometryData = async ({ testId }: { testId: string }) => {
    const response = await fetcher(POST_SPIROMETRY_ENDPOINT, {
        params: { test_id: testId },
    });
    return convertPostSpirometryDataFromAPI(response);
};

export const submitPostSprimetryData = async ({
    testId,
    values,
}: {
    testId: string;
    values: PostSpirometryData;
}) => {
    const response = await fetcher(
        POST_SPIROMETRY_ENDPOINT,
        {
            method: "PUT",
            body: JSON.stringify(values),
            headers: {
                "Content-Type": "application/json",
            },
            params: { test_id: testId },
        },
        true
    );

    return convertPostSpirometryDataFromAPI(response);
};

export const getPostSpirometryCalculatedValues = async ({
    value,
    fieldId,
    testId,
}: {
    value: Nullable<string | number>;
    fieldId: string;
    testId?: string;
}) => {
    if (!testId) {
        return null;
    }

    const values = {
        [fieldId]: { measured: value },
    };

    const response = await fetcher(
        POST_SPIROMETRY_ENDPOINT,
        {
            method: "PUT",
            body: JSON.stringify(values),
            headers: {
                "Content-Type": "application/json",
            },
            params: { test_id: testId },
        },
        true
    );

    const data = convertPostSpirometryDataFromAPI(response);

    return data as PostSpirometryData;
};

export const getPreSpirometryCalculatedValues = async ({
    value,
    fieldId,
    testId,
}: {
    value: Nullable<string | number>;
    fieldId: string;
    testId?: string;
}) => {
    if (!testId) {
        return null;
    }

    const values = {
        [fieldId]: { measured: value },
    };

    const response = await fetcher(
        PRE_SPIROMETRY_ENDPOINT,
        {
            method: "PUT",
            body: JSON.stringify(values),
            headers: {
                "Content-Type": "application/json",
            },
            params: { test_id: testId },
        },
        true
    );

    const data = convertPreSpirometryDataFromAPI(response);

    return data as PreSpirometryData;
};
